import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from '../../components/admin/Layout';
import api from '../../utils/api';
import { useToast } from '../../context/ToastContext';
import './EventForm.css';

const VENUE_TYPES = ['Online', 'Physical', 'Hybrid'];
const CURRENCIES  = ['GHS', 'USD', 'EUR', 'GBP', 'NGN', 'KES', 'ZAR'];

const emptySession = () => ({ label: '', start_date: '', end_date: '', start_time: '', end_time: '' });
const emptyDetail  = () => ({ heading: '', description: '' });

export default function EventForm() {
  const { id }   = useParams();
  const isEdit   = Boolean(id);
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [loading, setLoading] = useState(isEdit);
  const [saving,  setSaving]  = useState(false);
  const [imagePreview, setImagePreview] = useState(null);

  const [form, setForm] = useState({
    title: '', description: '', is_free: '1', price: '', currency: 'GHS',
    venue_type: 'Online', venue_detail: '', is_visible: '1',
    sessions: [emptySession()], details: [emptyDetail()], image: null,
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!isEdit) return;
    api.get(`/admin/events/${id}`)
      .then(r => {
        const e = r.data;
        setForm({
          title: e.title || '', description: e.description || '',
          is_free: e.is_free ? '1' : '0', price: e.price || '', currency: e.currency || 'GHS',
          venue_type: e.venue_type || 'Online', venue_detail: e.venue_details || '',
          is_visible: e.is_visible ? '1' : '0',
          sessions: e.sessions?.length ? e.sessions.map(s => ({
            label: s.session_label || '',
            start_date: s.start_date ? String(s.start_date).slice(0, 10) : '',
            end_date:   s.end_date ? String(s.end_date).slice(0, 10) : '',
            start_time: s.start_time ? String(s.start_time).slice(0, 5) : '',
            end_time:   s.end_time ? String(s.end_time).slice(0, 5) : '',
          })) : [emptySession()],
          details: e.details?.length ? e.details.map(d => ({ heading: d.heading, description: d.description })) : [emptyDetail()],
          image: null,
        });
        if (e.image_url) setImagePreview(e.image_url);
      })
      .catch(() => showToast('Failed to load event', 'error'))
      .finally(() => setLoading(false));
  }, [id, isEdit, showToast]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setSession = (i, k, v) => setForm(f => { const s = [...f.sessions]; s[i] = { ...s[i], [k]: v }; return { ...f, sessions: s }; });
  const addSession    = () => setForm(f => ({ ...f, sessions: [...f.sessions, emptySession()] }));
  const removeSession = (i) => setForm(f => ({ ...f, sessions: f.sessions.filter((_, idx) => idx !== i) }));
  const setDetail = (i, k, v) => setForm(f => { const d = [...f.details]; d[i] = { ...d[i], [k]: v }; return { ...f, details: d }; });
  const addDetail    = () => setForm(f => ({ ...f, details: [...f.details, emptyDetail()] }));
  const removeDetail = (i) => setForm(f => ({ ...f, details: f.details.filter((_, idx) => idx !== i) }));

  const handleImage = useCallback(e => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { showToast('Image must be under 5MB', 'warning'); return; }
    set('image', file);
    const reader = new FileReader();
    reader.onload = ev => setImagePreview(ev.target.result);
    reader.readAsDataURL(file);
  }, [showToast]);

  const validate = () => {
    const e = {};
    if (!form.title.trim()) e.title = 'Event title is required.';
    if (!form.venue_detail.trim()) e.venue_detail = 'Please specify the venue.';
    if (form.is_free === '0' && (!form.price || isNaN(form.price) || parseFloat(form.price) <= 0))
      e.price = 'Enter a valid price greater than 0.';
    form.sessions.forEach((s, i) => {
      if (!s.start_date) e[`s_start_${i}`] = 'Start date required.';
      if (s.end_date && s.start_date && s.end_date < s.start_date) e[`s_end_${i}`] = 'End date must be after start date.';
    });
    form.details.forEach((d, i) => {
      if (d.heading.trim() && !d.description.trim()) e[`d_desc_${i}`] = 'Add a description or remove this section.';
    });
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) { showToast('Please fix the highlighted fields', 'warning'); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('title', form.title.trim());
      fd.append('description', form.description.trim());
      fd.append('is_free', form.is_free);
      fd.append('price', form.is_free === '1' ? 0 : form.price);
      fd.append('currency', form.currency);
      fd.append('venue_type', form.venue_type);
      fd.append('venue_detail', form.venue_detail.trim());
      fd.append('is_visible', form.is_visible);
      fd.append('sessions', JSON.stringify(form.sessions.filter(s => s.start_date).map(s => ({
        label: s.label, start_date: s.start_date, end_date: s.end_date || s.start_date,
        start_time: s.start_time, end_time: s.end_time,
      }))));
      fd.append('details', JSON.stringify(form.details.filter(d => d.heading.trim())));
      if (form.image) fd.append('image', form.image);

      if (isEdit) {
        await api.put(`/admin/events/${id}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        showToast('Event updated successfully', 'success');
      } else {
        await api.post('/admin/events', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        showToast('Event created successfully', 'success');
      }
      navigate('/admin/events');
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to save event', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Layout><div className="page-loading"><div className="spinner" /></div></Layout>;

  return (
    <Layout>
      <div className="event-form-page">
        <div className="page-header">
          <div>
            <h1>{isEdit ? 'Edit Event' : 'Create New Event'}</h1>
            <p>{isEdit ? 'Update the event details below.' : 'Fill in the details to create a new event.'}</p>
          </div>
          <button className="btn btn-outline" onClick={() => navigate('/admin/events')}>← Back to Events</button>
        </div>

        <form onSubmit={handleSubmit} className="event-form" noValidate>
          <div className="form-grid">
            <div className="form-col">
              {/* Basic */}
              <div className="form-card">
                <h2 className="card-section-title">Basic Information</h2>
                <div className="form-group">
                  <label className="form-label">Event Title <span className="req">*</span></label>
                  <input className={`form-control ${errors.title ? 'is-invalid' : ''}`} value={form.title}
                    onChange={e => set('title', e.target.value)} placeholder="e.g. AI Innovation Summit 2026" />
                  {errors.title && <span className="invalid-feedback">{errors.title}</span>}
                </div>
                <div className="form-group">
                  <label className="form-label">Event Description</label>
                  <textarea className="form-control" rows={4} value={form.description}
                    onChange={e => set('description', e.target.value)} placeholder="Brief overview of the event..." />
                </div>
              </div>

              {/* Sessions */}
              <div className="form-card">
                <div className="card-section-header">
                  <h2 className="card-section-title">Schedule &amp; Sessions</h2>
                  <button type="button" className="btn btn-sm btn-outline" onClick={addSession}>+ Add Session</button>
                </div>
                <p className="form-hint" style={{ marginBottom: '1rem' }}>
                  Each session has a start and end date so multi-day runs work. Add several sessions for an event that
                  repeats on different dates (e.g. 20–22 Jun and 30 Jul–4 Aug).
                </p>
                {form.sessions.map((s, i) => (
                  <div className="session-block" key={i}>
                    <div className="session-block-header">
                      <span className="session-num">Session {i + 1}</span>
                      {form.sessions.length > 1 && (
                        <button type="button" className="btn-remove" onClick={() => removeSession(i)}>✕ Remove</button>
                      )}
                    </div>
                    <div className="form-group">
                      <label className="form-label">Label (optional)</label>
                      <input className="form-control" value={s.label}
                        onChange={e => setSession(i, 'label', e.target.value)} placeholder="e.g. Cohort 1 / Weekend Track" />
                    </div>
                    <div className="form-row-2">
                      <div className="form-group">
                        <label className="form-label">Start Date <span className="req">*</span></label>
                        <input type="date" className={`form-control ${errors[`s_start_${i}`] ? 'is-invalid' : ''}`}
                          value={s.start_date} onChange={e => setSession(i, 'start_date', e.target.value)} />
                        {errors[`s_start_${i}`] && <span className="invalid-feedback">{errors[`s_start_${i}`]}</span>}
                      </div>
                      <div className="form-group">
                        <label className="form-label">End Date</label>
                        <input type="date" className={`form-control ${errors[`s_end_${i}`] ? 'is-invalid' : ''}`}
                          value={s.end_date} min={s.start_date} onChange={e => setSession(i, 'end_date', e.target.value)} />
                        {errors[`s_end_${i}`] && <span className="invalid-feedback">{errors[`s_end_${i}`]}</span>}
                      </div>
                    </div>
                    <div className="form-row-2">
                      <div className="form-group">
                        <label className="form-label">Start Time</label>
                        <input type="time" className="form-control" value={s.start_time}
                          onChange={e => setSession(i, 'start_time', e.target.value)} />
                      </div>
                      <div className="form-group">
                        <label className="form-label">End Time</label>
                        <input type="time" className="form-control" value={s.end_time}
                          onChange={e => setSession(i, 'end_time', e.target.value)} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Details */}
              <div className="form-card">
                <div className="card-section-header">
                  <h2 className="card-section-title">Detail Sections</h2>
                  <button type="button" className="btn btn-sm btn-outline" onClick={addDetail}>+ Add Section</button>
                </div>
                <p className="form-hint" style={{ marginBottom: '1rem' }}>Optional structured sections like "About", "Speakers", "Agenda".</p>
                {form.details.map((d, i) => (
                  <div className="detail-block" key={i}>
                    <div className="session-block-header">
                      <span className="session-num">Section {i + 1}</span>
                      {form.details.length > 1 && (
                        <button type="button" className="btn-remove" onClick={() => removeDetail(i)}>✕ Remove</button>
                      )}
                    </div>
                    <div className="form-group">
                      <label className="form-label">Heading</label>
                      <input className="form-control" value={d.heading}
                        onChange={e => setDetail(i, 'heading', e.target.value)} placeholder="e.g. About the Event" />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Description</label>
                      <textarea rows={3} className={`form-control ${errors[`d_desc_${i}`] ? 'is-invalid' : ''}`}
                        value={d.description} onChange={e => setDetail(i, 'description', e.target.value)} placeholder="Describe this section..." />
                      {errors[`d_desc_${i}`] && <span className="invalid-feedback">{errors[`d_desc_${i}`]}</span>}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Sidebar */}
            <div className="form-col">
              <div className="form-card">
                <h2 className="card-section-title">Pricing</h2>
                <div className="radio-group">
                  {[['1', '🎉 Free Event'], ['0', '💳 Paid Event']].map(([val, lbl]) => (
                    <label key={val} className={`radio-opt ${form.is_free === val ? 'active' : ''}`}>
                      <input type="radio" value={val} checked={form.is_free === val} onChange={e => set('is_free', e.target.value)} /> {lbl}
                    </label>
                  ))}
                </div>
                {form.is_free === '0' && (
                  <div className="form-row-2" style={{ marginTop: '1rem' }}>
                    <div className="form-group">
                      <label className="form-label">Amount <span className="req">*</span></label>
                      <input type="number" min="0.01" step="0.01" className={`form-control ${errors.price ? 'is-invalid' : ''}`}
                        value={form.price} onChange={e => set('price', e.target.value)} placeholder="0.00" />
                      {errors.price && <span className="invalid-feedback">{errors.price}</span>}
                    </div>
                    <div className="form-group">
                      <label className="form-label">Currency</label>
                      <select className="form-control" value={form.currency} onChange={e => set('currency', e.target.value)}>
                        {CURRENCIES.map(c => <option key={c}>{c}</option>)}
                      </select>
                    </div>
                  </div>
                )}
              </div>

              <div className="form-card">
                <h2 className="card-section-title">Venue</h2>
                <div className="radio-group">
                  {VENUE_TYPES.map(vt => (
                    <label key={vt} className={`radio-opt ${form.venue_type === vt ? 'active' : ''}`}>
                      <input type="radio" value={vt} checked={form.venue_type === vt} onChange={e => set('venue_type', e.target.value)} />
                      {vt === 'Online' ? '💻' : vt === 'Physical' ? '📍' : '🌐'} {vt}
                    </label>
                  ))}
                </div>
                <div className="form-group" style={{ marginTop: '1rem' }}>
                  <label className="form-label">{form.venue_type === 'Online' ? 'Platform / Link' : 'Venue Address'} <span className="req">*</span></label>
                  <input className={`form-control ${errors.venue_detail ? 'is-invalid' : ''}`} value={form.venue_detail}
                    onChange={e => set('venue_detail', e.target.value)}
                    placeholder={form.venue_type === 'Online' ? 'e.g. Zoom link' : 'e.g. Accra Conference Centre'} />
                  {errors.venue_detail && <span className="invalid-feedback">{errors.venue_detail}</span>}
                </div>
              </div>

              <div className="form-card">
                <h2 className="card-section-title">Event Image</h2>
                <div className="image-upload-zone" onClick={() => document.getElementById('eventImageInput').click()}>
                  {imagePreview ? <img src={imagePreview} alt="Preview" className="image-preview" /> : (
                    <div className="upload-placeholder">
                      <svg width="40" height="40" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <p>Click to upload</p><small>JPG, PNG, WebP • Max 5MB</small>
                    </div>
                  )}
                </div>
                <input id="eventImageInput" type="file" accept="image/*" hidden onChange={handleImage} />
                {imagePreview && (
                  <button type="button" className="btn btn-sm btn-outline" style={{ marginTop: '0.5rem', width: '100%' }}
                    onClick={() => { setImagePreview(null); set('image', null); }}>Remove Image</button>
                )}
              </div>

              <div className="form-card">
                <h2 className="card-section-title">Visibility</h2>
                <div className="radio-group">
                  {[['1', '👁️ Visible to public'], ['0', '🔒 Hidden (admins only)']].map(([val, lbl]) => (
                    <label key={val} className={`radio-opt ${form.is_visible === val ? 'active' : ''}`}>
                      <input type="radio" value={val} checked={form.is_visible === val} onChange={e => set('is_visible', e.target.value)} /> {lbl}
                    </label>
                  ))}
                </div>
              </div>

              <div className="form-actions">
                <button type="button" className="btn btn-outline" onClick={() => navigate('/admin/events')}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? <><span className="spinner spinner-sm" /> Saving…</> : isEdit ? 'Update Event' : 'Create Event'}
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </Layout>
  );
}
